(() => {
  'use strict';

  window.debounce = window.debounce || function (fn, wait) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let trappedContainer = null;
  let previousFocus = null;
  let trapHandler = null;

  window.trapFocus = window.trapFocus || function (container, focusElement) {
    if (!container) return;
    window.removeTrapFocus();
    trappedContainer = container;
    previousFocus = document.activeElement;
    trapHandler = (event) => {
      if (event.key !== 'Tab') return;
      const elements = Array.from(trappedContainer.querySelectorAll(focusableSelector)).filter((el) => el.offsetParent !== null);
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', trapHandler);
    (focusElement || container.querySelector(focusableSelector) || container).focus?.();
  };

  window.removeTrapFocus = window.removeTrapFocus || function (returnFocus) {
    if (trapHandler) document.removeEventListener('keydown', trapHandler);
    trapHandler = null;
    trappedContainer = null;
    const target = returnFocus === false ? null : (returnFocus || previousFocus);
    target?.focus?.();
    previousFocus = null;
  };

  window.pauseAllMedia = window.pauseAllMedia || function () {
    document.querySelectorAll('video').forEach((video) => video.pause());
    document.querySelectorAll('iframe[src*="youtube"], iframe[src*="vimeo"]').forEach((frame) => {
      frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo' }), '*');
    });
  };

  window.fetchConfig = window.fetchConfig || function (type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
    };
  };

  async function cartState() {
    const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('No fue posible actualizar el carrito.');
    return response.json();
  }

  async function updateCartBubble() {
    try {
      const cart = await cartState();
      const link = document.getElementById('cart-icon-bubble');
      if (!link) return cart;
      let bubble = link.querySelector('.cart-count-bubble');
      if (cart.item_count > 0) {
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
          link.appendChild(bubble);
        }
        const visible = bubble.querySelector('span[aria-hidden="true"]');
        const hidden = bubble.querySelector('.visually-hidden');
        if (visible) visible.textContent = cart.item_count < 100 ? cart.item_count : '99+';
        if (hidden) hidden.textContent = `${cart.item_count} productos en el carrito`;
      } else {
        bubble?.remove();
      }
      return cart;
    } catch (error) {
      console.warn('[Zoom 3] No se pudo actualizar el contador del carrito.', error);
      return null;
    }
  }
  window.zoom3UpdateCartBubble = updateCartBubble;

  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', class QuantityInput extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';
        this.input = this.querySelector('input[type="number"]');
        this.addEventListener('click', (event) => {
          const button = event.target.closest('button[name="plus"], button[name="minus"]');
          if (!button || !this.input) return;
          event.preventDefault();
          const before = this.input.value;
          if (button.name === 'plus') this.input.stepUp(); else this.input.stepDown();
          if (before !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    });
  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', class CartDrawer extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', (event) => {
          if (event.target.closest('.cart-drawer__overlay, .drawer__close')) this.close();
        });
        this.addEventListener('keyup', (event) => {
          if (event.key === 'Escape') this.close();
        });
      }

      open(trigger) {
        this.activeElement = trigger || document.activeElement;
        this.classList.add('active');
        this.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
        const panel = this.querySelector('.drawer__inner');
        if (panel) window.trapFocus(panel, panel.querySelector('.drawer__close'));
      }

      close() {
        this.classList.remove('active');
        this.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
        window.removeTrapFocus(this.activeElement);
      }

      async refresh(openAfter = false) {
        const wasOpen = openAfter || this.classList.contains('active');
        const url = `${window.routes?.cart_url || '/cart'}?section_id=cart-drawer`;
        const response = await fetch(url, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('No fue posible recargar el carrito.');
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const replacement = doc.querySelector('cart-drawer');
        if (!replacement) throw new Error('La sección del carrito no devolvió contenido válido.');
        this.innerHTML = replacement.innerHTML;
        this.className = replacement.className;
        await updateCartBubble();
        if (wasOpen) this.open(this.activeElement);
      }
    });
  }

  async function changeCartLine(line, quantity, context) {
    const response = await fetch(`${window.routes?.cart_change_url || '/cart/change'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'No fue posible actualizar el carrito.');
    const drawer = document.querySelector('cart-drawer');
    if (context?.tagName === 'CART-DRAWER-ITEMS' && drawer?.refresh) {
      await drawer.refresh(true);
    } else {
      window.location.reload();
    }
  }

  const defineCartItems = (name) => {
    if (customElements.get(name)) return;
    customElements.define(name, class CartItems extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';
        this.addEventListener('change', async (event) => {
          const input = event.target.closest('input[data-index]');
          if (!input) return;
          input.disabled = true;
          try { await changeCartLine(input.dataset.index, input.value, this); }
          catch (error) { this.showError(error.message); input.disabled = false; }
        });
      }
      async updateQuantity(line, quantity) { return changeCartLine(line, quantity, this); }
      showError(message) {
        const box = this.querySelector('#CartDrawer-CartErrors, .cart-errors');
        if (box) box.textContent = message;
      }
    });
  };
  defineCartItems('cart-items');
  defineCartItems('cart-drawer-items');

  if (!customElements.get('cart-remove-button')) {
    customElements.define('cart-remove-button', class CartRemoveButton extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', async (event) => {
          event.preventDefault();
          const parent = this.closest('cart-items, cart-drawer-items');
          try { await parent?.updateQuantity(this.dataset.index, 0); }
          catch (error) { parent?.showError(error.message); }
        });
      }
    });
  }

  document.addEventListener('click', (event) => {
    const cartLink = event.target.closest('#cart-icon-bubble');
    const drawer = document.querySelector('cart-drawer');
    if (!cartLink || !drawer || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    drawer.open(cartLink);
  });
})();
